import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  Directive,
  ElementRef,
  Inject,
  Injector,
  isDevMode,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';

const PANE_ATTACH_OBSERVE_MS = 3000;

@Directive({
  standalone: true,
  selector: 'mat-select',
})
export class MatSelectOverlayPaneMinWidthDirective implements OnInit, OnDestroy {
  private openedSub?: Subscription;
  private lastPane: HTMLElement | null = null;
  private paneAttachObserver?: MutationObserver;
  private paneAttachTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly host: MatSelect,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly injector: Injector,
  ) {}

  ngOnInit(): void {
    this.openedSub = this.host.openedChange.subscribe((open) => this.onOpenedChange(open));
  }

  ngOnDestroy(): void {
    this.openedSub?.unsubscribe();
    this.teardownPaneAttachWatch();
    this.clearPaneMinWidth();
  }

  private onOpenedChange(open: boolean): void {
    if (!open) {
      this.teardownPaneAttachWatch();
      this.clearPaneMinWidth();
      return;
    }

    afterNextRender(() => this.applyTriggerMinWidthToOverlayPane(), { injector: this.injector });
  }

  private clearPaneMinWidth(): void {
    if (this.lastPane) {
      this.lastPane.style.minWidth = '';
      this.lastPane = null;
    }
  }

  private applyTriggerMinWidthToOverlayPane(): void {
    const triggerPx = Math.ceil(this.elementRef.nativeElement.getBoundingClientRect().width);
    if (triggerPx <= 0) {
      return;
    }

    const pane = this.findOverlayPane();
    if (pane) {
      this.applyMinWidthToPane(pane, triggerPx);
    } else {
      this.scheduleOverlayPaneAttach(triggerPx);
    }
  }

  private scheduleOverlayPaneAttach(triggerPx: number): void {
    this.teardownPaneAttachWatch();

    const tryApply = (): boolean => {
      const found = this.findOverlayPane();
      if (found) {
        this.applyMinWidthToPane(found, triggerPx);
        this.teardownPaneAttachWatch();
        return true;
      }
      return false;
    };

    if (tryApply()) {
      return;
    }

    this.paneAttachObserver = new MutationObserver((records) => {
      if (!records.some((r) => r.addedNodes.length > 0)) {
        return;
      }
      tryApply();
    });
    const overlayRoot =
      this.document.querySelector('.cdk-overlay-container') ?? this.document.body;
    this.paneAttachObserver.observe(overlayRoot, { childList: true, subtree: true });

    this.paneAttachTimeoutId = setTimeout(() => {
      this.teardownPaneAttachWatch();
      if (!this.lastPane && isDevMode()) {
        console.warn(
          `MatSelectOverlayPaneMinWidthDirective: overlay pane not found for mat-select id="${this.host.id}" within ${PANE_ATTACH_OBSERVE_MS}ms; min-width not applied.`,
        );
      }
    }, PANE_ATTACH_OBSERVE_MS);
  }

  private teardownPaneAttachWatch(): void {
    this.paneAttachObserver?.disconnect();
    this.paneAttachObserver = undefined;
    if (this.paneAttachTimeoutId != null) {
      clearTimeout(this.paneAttachTimeoutId);
      this.paneAttachTimeoutId = undefined;
    }
  }

  private findOverlayPane(): HTMLElement | null {
    const panel = this.getSelectPanelElement();
    return panel?.closest('.cdk-overlay-pane') ?? null;
  }

  private getSelectPanelElement(): HTMLElement | null {
    const byId = this.document.getElementById(`${this.host.id}-panel`);
    if (byId) {
      return byId;
    }
    const host = this.host as MatSelect & { panel?: ElementRef<HTMLElement> };
    return host.panel?.nativeElement ?? null;
  }

  private applyMinWidthToPane(pane: HTMLElement, triggerPx: number): void {
    this.clearPaneMinWidth();
    this.lastPane = pane;
    pane.style.minWidth = `${triggerPx}px`;
  }
}
